import React, { useEffect, useRef, useState } from "react";
import { PlayButton, ProgressPill, shuffled } from "../../grammar/shared";
import { Prompt, IDLE, SELECTED, WRONG } from "./shared";

/**
 * MatchPairsItem — two columns, Arabic words and English meanings, both
 * shuffled. Tap one of each to pair them; a match locks gold, a miss flashes
 * red and counts as a mistake. Correct when every pair is matched with at
 * most one mistake per four pairs (a single slip on a 5-pair board passes).
 */
export default function MatchPairsItem({ question, onAnswer, audio }) {
  const pairs = question.data.pairs.map((p, i) => ({ ...p, id: i }));
  const [left] = useState(() => shuffled(pairs));
  const [right] = useState(() => shuffled(pairs));
  const [selLeft, setSelLeft] = useState(null);
  const [selRight, setSelRight] = useState(null);
  const [matched, setMatched] = useState(() => new Set());
  const [wrong, setWrong] = useState(null); // {l, r}
  const [mistakes, setMistakes] = useState(0);
  const timer = useRef(null);
  const reported = useRef(false);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (matched.size >= pairs.length && !reported.current) {
      reported.current = true;
      const allowed = Math.floor(pairs.length / 4);
      onAnswer({ correct: mistakes <= allowed, answer: { mistakes } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matched]);

  const tryMatch = (l, r) => {
    if (l === r) {
      setMatched((prev) => new Set(prev).add(l));
      const pair = pairs[l];
      if (audio.audioAllowed) audio.play(pair.ar);
    } else {
      setMistakes((n) => n + 1);
      setWrong({ l, r });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setWrong(null), 600);
    }
    setSelLeft(null);
    setSelRight(null);
  };

  const tapLeft = (id) => {
    if (matched.has(id)) return;
    if (selRight !== null) tryMatch(id, selRight);
    else {
      setSelLeft(id);
      if (audio.audioAllowed) audio.play(pairs[id].ar);
    }
  };
  const tapRight = (id) => {
    if (matched.has(id)) return;
    if (selLeft !== null) tryMatch(selLeft, id);
    else setSelRight(id);
  };

  const cell = (pair, side) => {
    const isMatched = matched.has(pair.id);
    const isSel = side === "l" ? selLeft === pair.id : selRight === pair.id;
    const isWrong = wrong && (side === "l" ? wrong.l === pair.id : wrong.r === pair.id);
    if (isMatched) {
      return (
        <div key={pair.id} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl min-h-[52px]"
             style={{ background: "var(--gold-bg)", border: "1px solid var(--gold-dim)" }}>
          {side === "l" ? (
            <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.6]" style={{ color: "var(--parchment)" }}>{pair.ar}</span>
          ) : (
            <span className="text-sm" style={{ color: "var(--parchment)" }}>{pair.en}</span>
          )}
          {side === "l" && audio.audioAllowed
            ? <PlayButton text={pair.ar} playingText={audio.playingText} onPlay={audio.play} />
            : <span aria-hidden="true" style={{ color: "var(--gold-bright)" }}>✓</span>}
        </div>
      );
    }
    return (
      <button
        key={pair.id}
        type="button"
        onClick={() => (side === "l" ? tapLeft(pair.id) : tapRight(pair.id))}
        aria-pressed={isSel}
        className="px-3 py-2.5 rounded-xl min-h-[52px] transition-all hover:brightness-110 text-center"
        style={isWrong ? WRONG : isSel ? SELECTED : IDLE}
      >
        {side === "l" ? (
          <span dir="rtl" lang="ar" className="font-arabic text-xl leading-[1.6] block">{pair.ar}</span>
        ) : (
          <span className="text-sm block">{pair.en}</span>
        )}
      </button>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 mb-3">
        <Prompt>{question.prompt_en}</Prompt>
        <ProgressPill>{matched.size}/{pairs.length}</ProgressPill>
      </div>
      <div className="grid grid-cols-2 gap-3 max-w-xl mx-auto">
        <div className="flex flex-col gap-2.5">{left.map((p) => cell(p, "l"))}</div>
        <div className="flex flex-col gap-2.5">{right.map((p) => cell(p, "r"))}</div>
      </div>
      {mistakes > 0 && matched.size < pairs.length && (
        <p className="text-xs text-center mt-3 mb-0" style={{ color: "var(--parchment-dim)" }}>
          {mistakes} slip{mistakes > 1 ? "s" : ""} so far
        </p>
      )}
    </>
  );
}
